import React, { useEffect, useState } from 'react';
import { useGetCategoriesQuery, useGetSubcategoriesQuery } from '../redux/apiSlice';

const BasicInfoStep = ({ courseData, onInputChange, isEditMode = false }) => { // Add isEditMode prop
  const [localLoading, setLocalLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [subcategoriesError, setSubcategoriesError] = useState(null);
  const [prevCategory, setPrevCategory] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true); // Track initial load

  // Fetch categories from API
  const { 
    data: categoriesData, 
    isLoading: isCategoriesLoading, 
    isError: isCategoriesError,
    error: categoriesApiError 
  } = useGetCategoriesQuery({ page: 1, limit: 100 });

  // Fetch subcategories based on selected category
  const { 
    data: subcategoriesData, 
    isLoading: isSubcategoriesLoading,
    isError: isSubcategoriesError,
    error: subcategoriesApiError,
    refetch: refetchSubcategories
  } = useGetSubcategoriesQuery(
    { 
      categoryId: courseData.category || null,
      page: 1, 
      limit: 100 
    },
    {
      skip: !courseData.category,
    }
  );

  // Handle API errors
  useEffect(() => {
    if (isCategoriesError && categoriesApiError) {
      console.error('❌ Categories API Error:', categoriesApiError);
      let errorMessage = 'Failed to load categories. Please try again.';
      
      if (categoriesApiError.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (categoriesApiError.status === 404) {
        errorMessage = 'Categories not found.';
      } else if (categoriesApiError.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (categoriesApiError.data?.message) {
        errorMessage = categoriesApiError.data.message;
      }
      
      setCategoriesError(errorMessage);
    } else {
      setCategoriesError(null);
    }

    if (isSubcategoriesError && subcategoriesApiError) {
      console.error('❌ Subcategories API Error:', subcategoriesApiError);
      let errorMessage = 'Failed to load subcategories. Please try again.';
      
      if (subcategoriesApiError.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (subcategoriesApiError.status === 404) {
        errorMessage = 'Subcategories not found for this category.';
      } else if (subcategoriesApiError.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (subcategoriesApiError.data?.message) {
        errorMessage = subcategoriesApiError.data.message;
      }
      
      setSubcategoriesError(errorMessage);
    } else {
      setSubcategoriesError(null);
    }
  }, [isCategoriesError, categoriesApiError, isSubcategoriesError, subcategoriesApiError]);

  // Refetch subcategories only when category actually changes from user interaction
  useEffect(() => {
    if (initialLoad) {
      // Skip on initial load when editing - we want to keep the loaded subcategory
      setInitialLoad(false);
      console.log('📝 Initial load - preserving subcategory:', courseData.subCategory);
    } else if (courseData.category && courseData.category !== prevCategory) {
      console.log('🔄 Category changed from', prevCategory, 'to', courseData.category, '- refetching subcategories');
      setPrevCategory(courseData.category);
      
      // Only reset subcategory if NOT in edit mode or if this is a user-initiated change
      if (!isEditMode || prevCategory === null) {
        onInputChange('subCategory', ''); // Reset subcategory when category changes
      }
    }
  }, [courseData.category, prevCategory, onInputChange, isEditMode, initialLoad, courseData.subCategory]);

  // Safely extract categories from API response
  const extractCategories = () => {
    try {
      if (!categoriesData) return [];
      if (Array.isArray(categoriesData)) return categoriesData;
      if (categoriesData.categories && Array.isArray(categoriesData.categories)) return categoriesData.categories;
      if (categoriesData.data && Array.isArray(categoriesData.data)) return categoriesData.data;
      return [];
    } catch (error) {
      console.error('Error extracting categories:', error);
      return [];
    }
  };

  // Safely extract subcategories from API response
  const extractSubcategories = () => {
    try {
      if (!subcategoriesData) return [];
      if (Array.isArray(subcategoriesData)) return subcategoriesData;
      if (subcategoriesData.subcategories && Array.isArray(subcategoriesData.subcategories)) return subcategoriesData.subcategories;
      if (subcategoriesData.data && Array.isArray(subcategoriesData.data)) return subcategoriesData.data;
      return [];
    } catch (error) {
      console.error('Error extracting subcategories:', error);
      return [];
    }
  };

  const categories = extractCategories();
  console.log('📊 Loaded categories from API:', categories.length);

  const subcategories = extractSubcategories();
  console.log('📊 Loaded subcategories from API:', subcategories.length);

  // Find the current subcategory object
  const currentSubcategory = subcategories.find(sub => String(sub.id) === String(courseData.subCategory));
  console.log('🎯 Current subcategory object:', currentSubcategory);
  console.log('🎯 Course data subCategory:', courseData.subCategory);
  console.log('🎯 Is edit mode:', isEditMode);

  const levels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'all', label: 'All Levels' }
  ];

  const languages = [
    { value: 'english', label: 'English' },
    { value: 'spanish', label: 'Spanish' },
    { value: 'french', label: 'French' },
    { value: 'german', label: 'German' },
    { value: 'hindi', label: 'Hindi' }
  ];

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    console.log('🎯 Category selected:', categoryId);
    onInputChange('category', categoryId);
  };

  const handleSubCategoryChange = (e) => {
    const subCategoryId = e.target.value;
    console.log('🎯 Subcategory selected:', subCategoryId);
    onInputChange('subCategory', subCategoryId);
  };

  // Helper function to safely render text content
  const safeRenderText = (text) => {
    if (typeof text === 'string') return text;
    if (typeof text === 'number') return String(text);
    if (text === null || text === undefined) return '';
    if (typeof text === 'object') {
      // Try to extract meaningful content from object
      return text.content || text.name || text.title || text.label || JSON.stringify(text);
    }
    return String(text);
  };

  // Show loading state
  if (isCategoriesLoading) {
    return (
      <div className="p-8 animate-fade-in">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Basic Information</h2>
        <div className="flex justify-center items-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading categories...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Course Basic Information</h2>
      
      <div className="space-y-6">
        {/* Course Title */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Course Title *
          </label>
          <input
            type="text"
            value={safeRenderText(courseData.title)}
            onChange={(e) => onInputChange('title', e.target.value)}
            placeholder="e.g., Advanced React Development"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
            required
          />
        </div>

        {/* Course Subtitle */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Course Subtitle
          </label>
          <input
            type="text"
            value={safeRenderText(courseData.subtitle)}
            onChange={(e) => onInputChange('subtitle', e.target.value)}
            placeholder="Brief description of what students will learn"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
          />
        </div>

        {/* Course Description */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Course Description *
          </label>
          <textarea
            value={safeRenderText(courseData.description)}
            onChange={(e) => onInputChange('description', e.target.value)}
            placeholder="Detailed description of your course..."
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 resize-none"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Category */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category *
            </label>
            <div className="relative">
              <select
                value={safeRenderText(courseData.category) || ''}
                onChange={handleCategoryChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 appearance-none"
                required
                disabled={isCategoriesLoading}
              >
                <option value="">{isCategoriesLoading ? 'Loading categories...' : 'Select Category'}</option>
                {categories.map(category => {
                  const categoryId = safeRenderText(category.id);
                  const categoryName = safeRenderText(category.name);
                  return (
                    <option key={categoryId} value={categoryId}>
                      {categoryName}
                    </option>
                  );
                })}
              </select>
              {isCategoriesLoading && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                </div>
              )}
            </div>
            {categoriesError && (
              <p className="mt-1 text-sm text-red-600">{safeRenderText(categoriesError)}</p>
            )}
            {categories.length === 0 && !isCategoriesLoading && !categoriesError && (
              <p className="mt-1 text-sm text-yellow-600">No categories available. Please contact admin.</p>
            )}
          </div>

          {/* Sub Category */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sub Category
            </label>
            <div className="relative">
              <select
                value={safeRenderText(courseData.subCategory) || ''}
                onChange={handleSubCategoryChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300 appearance-none"
                disabled={!courseData.category || isSubcategoriesLoading}
              >
                <option value="">
                  {!courseData.category 
                    ? 'Select a category first' 
                    : isSubcategoriesLoading 
                      ? 'Loading subcategories...' 
                      : 'Select Sub Category'
                  }
                </option>
                {subcategories.map(subcategory => {
                  const subcategoryId = safeRenderText(subcategory.id);
                  const subcategoryName = safeRenderText(subcategory.name);
                  return (
                    <option key={subcategoryId} value={subcategoryId}>
                      {subcategoryName}
                    </option>
                  );
                })}
              </select>
              {isSubcategoriesLoading && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                </div>
              )}
            </div>
            {subcategoriesError && (
              <p className="mt-1 text-sm text-red-600">{safeRenderText(subcategoriesError)}</p>
            )}
            {subcategories.length === 0 && courseData.category && !isSubcategoriesLoading && !subcategoriesError && (
              <p className="mt-1 text-sm text-yellow-600">No subcategories available for this category.</p>
            )}
            {!courseData.category && (
              <p className="mt-1 text-sm text-gray-500">Select a category to see subcategories</p>
            )}
          </div>

          {/* Level */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Level *
            </label>
            <select
              value={safeRenderText(courseData.level)}
              onChange={(e) => onInputChange('level', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
            >
              {levels.map(level => (
                <option key={level.value} value={level.value}>{safeRenderText(level.label)}</option>
              ))}
            </select>
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Language *
            </label>
            <select
              value={safeRenderText(courseData.language)}
              onChange={(e) => onInputChange('language', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
            >
              {languages.map(language => (
                <option key={language.value} value={language.value}>{safeRenderText(language.label)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Debug Information (remove in production) */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Debug Information:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600">
                <span className="font-medium">Selected Category ID:</span> {safeRenderText(courseData.category) || 'None'}
                {courseData.category && categories.find(c => safeRenderText(c.id) === safeRenderText(courseData.category)) && (
                  <span className="ml-2 text-green-600">
                    ({safeRenderText(categories.find(c => safeRenderText(c.id) === safeRenderText(courseData.category)).name)})
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Selected Subcategory ID:</span> {safeRenderText(courseData.subCategory) || 'None'}
                {courseData.subCategory && subcategories.find(s => safeRenderText(s.id) === safeRenderText(courseData.subCategory)) && (
                  <span className="ml-2 text-green-600">
                    ({safeRenderText(subcategories.find(s => safeRenderText(s.id) === safeRenderText(courseData.subCategory)).name)})
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Previous Category:</span> {safeRenderText(prevCategory) || 'None'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Is Edit Mode:</span> {safeRenderText(isEditMode ? 'Yes' : 'No')}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Initial Load:</span> {safeRenderText(initialLoad ? 'Yes' : 'No')}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600">
                <span className="font-medium">Categories Loaded:</span> {safeRenderText(categories.length)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Subcategories Loaded:</span> {safeRenderText(subcategories.length)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Current Subcategory Found:</span> {safeRenderText(currentSubcategory ? 'Yes' : 'No')}
                {currentSubcategory && (
                  <span className="ml-2 text-green-600">
                    (ID: {safeRenderText(currentSubcategory.id)}, Name: {safeRenderText(currentSubcategory.name)})
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Category Loading:</span> {safeRenderText(isCategoriesLoading ? 'Yes' : 'No')}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                <span className="font-medium">Subcategory Loading:</span> {safeRenderText(isSubcategoriesLoading ? 'Yes' : 'No')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoStep;